import React, { useState, useEffect, useRef } from 'react'
import { useHistory, useLocation } from 'react-router-dom'
import Grid from '@mui/material/Grid'
import Paper from '@mui/material/Paper'
import Box from '@mui/material/Box'
import TextField from '@mui/material/TextField'
import FormControl from '@mui/material/FormControl'
import MenuItem from '@mui/material/MenuItem'
import Typography from '@mui/material/Typography'
import Title from '../../component/Title'
import API from '../../component/Axios'
import Snackbars from '../../component/Snackbars'
import BottomButtonGroup from '../../component/BottomButtonGroup'
import Spiner from '../../component/Spiner'
import ListEmptyState from '../../component/ListEmptyState'
import { FORM_IMAGE_PREVIEW_FRAME_SX, FORM_PANEL_SX } from '../../component/formSurfaceStyles'
import { createListLinkTarget, getLocationState } from '../../utils/listPageState'
import {
    applyAppGifticonDraft,
    clearAppGifticonDraft,
    createAppGifticonDraftPayload,
    createEditAppGifticonDraftKey,
    readAppGifticonDraft,
    saveAppGifticonDraft,
} from './appGifticonDraftUtils'

// 상세 조회 실패 시에도 폼 기본값을 명확하게 되돌릴 수 있게 공통 초기값을 사용한다.
const createDefaultParams = () => ({
    goodsCode: '',
    goodsName: '',
    brandName: '',
    goodsStateCd: '',
    salePrice: 0,
    limitDay: 0,
    validPrdDay: '',
    goodsImgB: '',
    content: '',
    status: 1,
})

const PANEL_SX = FORM_PANEL_SX

function FormSection({ title, description, children, sx = {} }) {
    return (
        <Paper variant="outlined" sx={{ ...PANEL_SX, ...sx }}>
            <Box sx={{ mb: 1.5 }}>
                <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
                    {title}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                    {description}
                </Typography>
            </Box>
            {children}
        </Paper>
    )
}

function GifticonImagePreview({ imageSrc }) {
    return (
        <FormSection
            title="상품 이미지"
            description="앱에 노출되는 기프티콘 상품 이미지를 확인합니다."
            sx={{ height: '100%' }}
        >
            <Box
                sx={{
                    ...FORM_IMAGE_PREVIEW_FRAME_SX,
                    minHeight: 360,
                    height: 'auto',
                    maxHeight: 520,
                    overflow: 'auto',
                }}
            >
                {imageSrc ? (
                    <Box
                        component="img"
                        src={imageSrc}
                        alt="기프티콘 상품 이미지"
                        sx={{
                            width: '100%',
                            maxHeight: 520,
                            objectFit: 'contain',
                            display: 'block',
                        }}
                    />
                ) : (
                    <Typography variant="body2" color="text.secondary">
                        등록된 이미지가 없습니다.
                    </Typography>
                )}
            </Box>
        </FormSection>
    )
}

export default function AppGifticon(props) {
    const history = useHistory()
    const location = useLocation()
    const locationState = getLocationState(location)
    const id = props.match.params.goodsCode
    const listCurPage = locationState.curPage || 1
    const searchText = locationState.searchText || ''
    const searchSelect = locationState.searchSelect || { searchStatus: '' }
    const listSearch = location.search || ''
    const draftKey = createEditAppGifticonDraftKey(id)
    const [loading, setLoading] = useState(true)
    const [saving, setSaving] = useState(false)
    const [draftReady, setDraftReady] = useState(false)
    const [params, setParams] = useState(() => createDefaultParams())
    const [mainImage, setMainImage] = useState('')
    const [openSnackbar, setOpenSnackbar] = useState(locationState.openSnackbar || false)
    const [severitySnackbar, setSeveritySnackbar] = useState(locationState.severitySnackbar || 'info')
    const [messageSnackbar, setMessageSnackbar] = useState(locationState.messageSnackbar || '')
    const [detailErrorMessage, setDetailErrorMessage] = useState('')
    const initialDraftSnapshotRef = useRef('')

    useEffect(() => {
        const getData = async () => {
            if (!id) {
                const errorMessage = '필수 파라미터가 없어 기프티콘 상세를 불러올 수 없습니다.'
                setParams(createDefaultParams())
                setMainImage('')
                setDraftReady(false)
                setDetailErrorMessage(errorMessage)
                setOpenSnackbar(true)
                setSeveritySnackbar('error')
                setMessageSnackbar(errorMessage)
                setLoading(false)
                return
            }

            setLoading(true)
            setDraftReady(false)
            setDetailErrorMessage('')

            try {
                const { data } = await API.post('/api/v1/admin/gifticon/getDetail', { goodsCode: id })
                if (data.code === 200) {
                    const baseParams = data.data || {}

                    if (!baseParams.goodsCode) {
                        setParams(createDefaultParams())
                        setMainImage('')
                        setDetailErrorMessage('기프티콘 상세 정보를 찾을 수 없습니다.')
                        setLoading(false)
                        return
                    }

                    const storedDraft = readAppGifticonDraft(draftKey)
                    const restoredDraft = applyAppGifticonDraft({
                        baseParams,
                        draft: storedDraft,
                    })

                    initialDraftSnapshotRef.current = JSON.stringify(createAppGifticonDraftPayload({
                        params: baseParams,
                    }))

                    setParams(restoredDraft.params)
                    setMainImage(baseParams.goodsImgS ? `${baseParams.goodsImgS}` : '/images/noimage.jpg')
                    setDetailErrorMessage('')
                    setDraftReady(true)
                } else if (data.code === 403) {
                    history.push('/login')
                    return
                } else {
                    const errorMessage = data.message || '기프티콘 상세를 불러오지 못했습니다.'
                    setParams(createDefaultParams())
                    setMainImage('')
                    setDetailErrorMessage(errorMessage)
                    setOpenSnackbar(true)
                    setSeveritySnackbar('error')
                    setMessageSnackbar(errorMessage)
                }
            } catch (_error) {
                const errorMessage = '기프티콘 상세를 불러오지 못했습니다.'
                setParams(createDefaultParams())
                setMainImage('')
                setDetailErrorMessage(errorMessage)
                setOpenSnackbar(true)
                setSeveritySnackbar('error')
                setMessageSnackbar(errorMessage)
            } finally {
                setLoading(false)
            }
        }
        getData()
    }, [draftKey, history, id])

    useEffect(() => {
        if (!draftReady || !params?.goodsCode) {
            return
        }

        // 기프티콘 상세는 목록 query를 건드리지 않고 판매상태 draft만 sessionStorage에 보존한다.
        const nextDraft = createAppGifticonDraftPayload({
            params,
        })
        const nextDraftSnapshot = JSON.stringify(nextDraft)

        if (nextDraftSnapshot === initialDraftSnapshotRef.current) {
            clearAppGifticonDraft(draftKey)
            return
        }

        saveAppGifticonDraft(draftKey, nextDraft)
    }, [draftKey, draftReady, params])

    const handleClose = () => {
        setOpenSnackbar(false)
    }

    // 상세 저장 후에도 원래 목록 query와 필터를 그대로 유지한 채 복귀한다.
    const moveToList = (snackbarState) => {
        clearAppGifticonDraft(draftKey)
        history.push(createListLinkTarget({
            pathname: '/appGifticons',
            search: listSearch,
            searchText: searchText,
            searchSelect: searchSelect,
            curPage: listCurPage,
            ...(snackbarState ? {
                openSnackbar: true,
                severitySnackbar: snackbarState.severity,
                messageSnackbar: snackbarState.message,
            } : {})
        }))
    }

    const handleUpdateField = (e) => {
        setParams({
            ...params,
            [e.target.name]: e.target.value
        })
    }

    const updateData = async () => {
        if (saving) {
            return
        }

        try {
            setSaving(true)

            const { data } = await API.post('/api/v1/admin/gifticon/update', { goodsCode: params.goodsCode, status: params.status })
            if (data.code === 200) {
                moveToList({ severity: 'success', message: '기프티콘 판매상태를 저장했습니다.' })
            } else if (data.code === 403) {
                history.push('/login')
            } else {
                setOpenSnackbar(true)
                setSeveritySnackbar('error')
                setMessageSnackbar(data.message)
            }
        } catch (_error) {
            setOpenSnackbar(true)
            setSeveritySnackbar('error')
            setMessageSnackbar('기프티콘 판매상태를 저장하지 못했습니다.')
        } finally {
            setSaving(false)
        }
    }

    const handleSubmit = (e) => {
        e.preventDefault()
        updateData()
    }

    // 상세 조회 실패 시 목록 화면과 같은 오류 빈 상태 안내를 사용한다.
    const renderDetailErrorState = () => (
        <ListEmptyState
            variant="error"
            title="기프티콘 상세를 불러오지 못했습니다."
            description={detailErrorMessage || '목록으로 돌아가 다시 확인하세요.'}
            action={(
                <BottomButtonGroup variant="emptyStateAction" onBackClick={() => moveToList()} backLabel="목록으로" />
            )}
            sx={{ mt: 2 }}
        />
    )

    if (loading) {
        return (
            <Grid item xs={12}>
                <Spiner/>
                {openSnackbar && <Snackbars open={openSnackbar} severity={severitySnackbar} message={messageSnackbar} handleClose={handleClose}/>}
            </Grid>
        )
    }

    if (!params?.goodsCode) {
        return (
            <Grid item xs={12}>
                <Paper sx={{ p: 2, display: 'flex', flexDirection: 'column' }}>
                    <Title>기프티콘 상세</Title>
                    {renderDetailErrorState()}
                    {openSnackbar && <Snackbars open={openSnackbar} severity={severitySnackbar} message={messageSnackbar} handleClose={handleClose}/>}
                </Paper>
            </Grid>
        )
    }

    return (
        <Grid item xs={12}>
            <Paper sx={{ p: 2, display: 'flex', flexDirection: 'column' }}>
                <Title>기프티콘 상세</Title>
                <Box component="form" encType="multipart/form-data" onSubmit={handleSubmit} sx={{ opacity: saving ? 0.6 : 1 }} >
                    <Grid container spacing={2} sx={{ marginTop: 2 }}>
                        <Grid item xs={12} md={4}>
                            <GifticonImagePreview imageSrc={mainImage}/>
                        </Grid>
                        <Grid item xs={12} md={8}>
                            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                                <FormSection
                                    title="상품 정보"
                                    description="상품 기본 정보와 판매 여부를 확인하고 판매상태만 변경합니다."
                                >
                                    <Grid container spacing={2}>
                                        <Grid item xs={12} sm={4}>
                                            <FormControl fullWidth>
                                                <TextField
                                                    required
                                                    label="상품코드"
                                                    id="goodsCode"
                                                    name="goodsCode"
                                                    value={params.goodsCode || ''}
                                                    onChange={handleUpdateField}
                                                    size="small"
                                                    InputProps={{ readOnly: true }}
                                                />
                                            </FormControl>
                                        </Grid>
                                        <Grid item xs={12} sm={4}>
                                            <FormControl fullWidth>
                                                <TextField
                                                    required
                                                    label="브랜드명"
                                                    id="brandName"
                                                    name="brandName"
                                                    value={params.brandName || ''}
                                                    onChange={handleUpdateField}
                                                    size="small"
                                                    InputProps={{ readOnly: true }}
                                                />
                                            </FormControl>
                                        </Grid>
                                        <Grid item xs={12} sm={4}>
                                            <FormControl fullWidth>
                                                <TextField
                                                    required
                                                    select
                                                    label="판매여부"
                                                    id="status"
                                                    name="status"
                                                    value={params.status}
                                                    size="small"
                                                    sx={{ minWidth: 150 }}
                                                    onChange={handleUpdateField}
                                                    disabled={saving}
                                                >
                                                    <MenuItem value={1}>활성화</MenuItem>
                                                    <MenuItem value={0}>비활성화</MenuItem>
                                                </TextField>
                                            </FormControl>
                                        </Grid>
                                        <Grid item xs={12}>
                                            <FormControl fullWidth>
                                                <TextField
                                                    required
                                                    label="상품명"
                                                    id="goodsName"
                                                    name="goodsName"
                                                    value={params.goodsName || ''}
                                                    onChange={handleUpdateField}
                                                    size="small"
                                                    InputProps={{ readOnly: true }}
                                                />
                                            </FormControl>
                                        </Grid>
                                        <Grid item xs={12} sm={4}>
                                            <FormControl fullWidth>
                                                <TextField
                                                    required
                                                    label="가격"
                                                    id="salePrice"
                                                    name="salePrice"
                                                    value={params.salePrice}
                                                    onChange={handleUpdateField}
                                                    size="small"
                                                    InputProps={{ readOnly: true }}
                                                />
                                            </FormControl>
                                        </Grid>
                                        <Grid item xs={12} sm={4}>
                                            <FormControl fullWidth>
                                                <TextField
                                                    label="유효기간(일수)"
                                                    id="limitDay"
                                                    name="limitDay"
                                                    value={params.limitDay || 0}
                                                    onChange={handleUpdateField}
                                                    size="small"
                                                    InputProps={{ readOnly: true }}
                                                />
                                            </FormControl>
                                        </Grid>
                                        <Grid item xs={12} sm={4}>
                                            <FormControl fullWidth>
                                                <TextField
                                                    label="유효기간(일자)"
                                                    id="validPrdDay"
                                                    name="validPrdDay"
                                                    value={params.validPrdDay || 0}
                                                    onChange={handleUpdateField}
                                                    size="small"
                                                    InputProps={{ readOnly: true }}
                                                />
                                            </FormControl>
                                        </Grid>
                                    </Grid>
                                </FormSection>
                                <FormSection
                                    title="상품 설명"
                                    description="기프티콘 상품 상세 설명을 확인합니다."
                                >
                                    <FormControl fullWidth>
                                        <TextField
                                            label="설명"
                                            multiline
                                            rows={7}
                                            name="content"
                                            value={params.content || ''}
                                            onChange={handleUpdateField}
                                            size="small"
                                            InputProps={{ readOnly: true }}
                                        />
                                    </FormControl>
                                </FormSection>
                            </Box>
                        </Grid>
                    </Grid>
                    <BottomButtonGroup
                        visible={true}
                        submitDisabled={saving}
                        submitLabel={saving ? '저장 중...' : '저장'}
                        backDisabled={saving}
                        onBackClick={() => moveToList()}
                    />
                </Box>
                {openSnackbar && <Snackbars open={openSnackbar} severity={severitySnackbar} message={messageSnackbar} handleClose={handleClose}/>}
            </Paper>
        </Grid>
    )
}
